// @flow
import React from 'react';
import { I18n } from '@lingui/react';
import styled, { type StyledComponent } from 'styled-components';

import type { Address } from '@rover/react-lib/types';
import addressFactory from '@rover/react-lib/factories/addressFactory';
import { required } from '@rover/react-lib/components/utils/Form/validation/validators';
import { isStateRequired } from '@rover/react-lib/utils/address';

import SuccessOrErrorAlert from '../../notifications/SuccessOrErrorAlert';

import AddressFormCountryField from './AddressFormCountryField';
import AddressFormLine1Field from './AddressFormLine1Field';
import AddressFormLine2Field from './AddressFormLine2Field';
import AddressFormZipcodeField from './AddressFormZipcodeField';
import AddressFormCityField from './AddressFormCityField';
import AddressFormStateField from './AddressFormStateField';

const FlexRow: StyledComponent<{}> = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  flex-wrap: wrap;
  align-items: flex-end;
`;

export type Props = {
  address: Address,
  onChange: ($Shape<Address>) => void,
  onDismiss?: Function,
  errorMessage: ?string,
  successMessage: string,
  validationErrors: Object,
  postalRegExp?: ?RegExp,
  supportedCountryCodes: Array<string>,
};

const AddressForm = ({
  address,
  onChange,
  onDismiss,
  errorMessage,
  successMessage,
  validationErrors,
  postalRegExp,
  supportedCountryCodes,
  ...other
}: Props) => (
  <I18n>
    {() => {
      const isUS = address.countryCode === 'US';
      const showStateField = isStateRequired(address.countryCode);
      return (
        <div {...other}>
          <SuccessOrErrorAlert
            error={errorMessage}
            success={successMessage}
            onDismiss={onDismiss}
          />
          <AddressFormCountryField
            value={address.countryCode}
            onChange={countryCode => onChange({ countryCode })}
            validationError={validationErrors.address.countryCode}
            supportedCountryCodes={supportedCountryCodes}
          />
          <FlexRow>
            <AddressFormLine1Field
              value={address.addressLine1}
              onChange={addressLine1 => onChange({ addressLine1 })}
              validationError={validationErrors.address.addressLine1}
            />
            <AddressFormLine2Field
              value={address.addressLine2}
              onChange={addressLine2 => onChange({ addressLine2 })}
              validationError={validationErrors.address.addressLine2}
              isUS={isUS}
            />
          </FlexRow>
          <FlexRow>
            <AddressFormCityField
              value={address.city}
              onChange={city => onChange({ city })}
              validationError={validationErrors.address.city}
              isUS={isUS}
            />
            {showStateField && (
              <AddressFormStateField
                value={address.state}
                onChange={state => onChange({ state })}
                validationError={validationErrors.address.state}
                isUS={isUS}
              />
            )}
            <AddressFormZipcodeField
              value={address.zip}
              onChange={zip => onChange({ zip })}
              validationError={validationErrors.address.zip}
              postalRegExp={postalRegExp}
              isUS={isUS}
            />
          </FlexRow>
        </div>
      );
    }}
  </I18n>
);

AddressForm.getValidators = (data: Address) => ({
  address: {
    countryCode: required,
    addressLine1: required,
    zip: required,
    city: required,
    state: () => (data && isStateRequired(data.countryCode) ? required() : null),
  },
});

AddressForm.defaultProps = {
  address: addressFactory(),
  errorMessage: '',
  successMessage: '',
  validationErrors: { address: {} },
  onDismiss: undefined,
  postalRegExp: null,
  supportedCountryCodes: [],
};

export default AddressForm;



// WEBPACK FOOTER //
// ../react-lib/src/components/forms/AddressForm/AddressForm.jsx